// src/App.js
// GR33D-SIMPLIFIED-V2: [2025-03-19] - Mise à jour avec Footer intégré

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Header from './components/Header';
import Footer from './components/Footer'; // Import du Footer
import ErrorBoundary from './components/ErrorBoundary';
import NotificationContainer from './components/common/Notification';
import { ProtectedRoute } from './components/routes/ProtectedRoute';
import Dashboard from './components/Dashboard';

// Custom Hooks
import { useWeb3 } from './context/Web3Context';

// Constants & Utilities
import { AUTHORIZED_VESTING_WALLETS, NETWORK_CONFIG } from './constants/contract';

// Admin address constant
const ADMIN_ADDRESS = "0x5A46d0F5bbce72D9665689cDAe9993824260b882";

// Lazy-loaded components for code splitting
const About = React.lazy(() => import('./components/AboutUs/AboutUs'));
const Home = React.lazy(() => import('./components/Home'));
const Roadmap = React.lazy(() => import('./components/Roadmap'));
const Whitepaper = React.lazy(() => import('./components/Whitepaper'));
const Contacts = React.lazy(() => import('./components/Contacts'));
const ArtMarketplace = React.lazy(() => import('./components/ArtMarketplace'));
const Staking = React.lazy(() => import('./components/Staking'));
const Liquidity = React.lazy(() => import('./components/Liquidity'));
const Vesting = React.lazy(() => import('./components/Vesting'));
const Admin = React.lazy(() => import('./components/Admin')); // Nouvelle page Admin

// Styles
import './App.css';
import './components/ui/ui.css';

const App = () => {
    // Get Web3 context
    const { 
        chainId, 
        provider
    } = useWeb3();
    
    // Network warning
    const [networkError, setNetworkError] = useState(null);

    // Check for network mismatch
    useEffect(() => {
        const checkNetwork = async () => {
            if (provider && chainId && chainId !== NETWORK_CONFIG.CHAIN_ID) {
                setNetworkError('Please connect to Ethereum Mainnet');
            } else {
                setNetworkError(null);
            }
        };

        checkNetwork();
    }, [provider, chainId]);

    // Loading fallback for lazy-loaded components
    const LoadingFallback = () => (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading content...</p>
        </div>
    );

    return (
        <ErrorBoundary>
            <Router>
                <div className="App">
                    {/* Network Warning */}
                    <AnimatePresence>
                        {networkError && (
                            <motion.div 
                                className="network-warning"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                            >
                                {networkError}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Système de notifications */}
                    <NotificationContainer />

                    {/* Header */}
                    <Header />

                    {/* Main Content */}
                    <main>
                        <Suspense fallback={<LoadingFallback />}>
                            <Routes>
                                {/* Public Routes */}
                                <Route path="/" element={<Home />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/roadmap" element={<Roadmap />} />
                                <Route path="/whitepaper" element={<Whitepaper />} />
                                <Route path="/art" element={<ArtMarketplace />} />
                                <Route path="/contacts" element={<Contacts />} />
                                
                                {/* Protected Routes */}
                                <Route 
                                    path="/staking" 
                                    element={
                                        <ProtectedRoute 
                                            element={Staking}
                                            requiresAuth={true}
                                        />
                                    } 
                                />
                                <Route 
                                    path="/liquidity" 
                                    element={
                                        <ProtectedRoute 
                                            element={Liquidity}
                                            requiresAuth={true}
                                        />
                                    } 
                                />
                                <Route 
                                    path="/vesting" 
                                    element={
                                        <ProtectedRoute 
                                            element={Vesting}
                                            requiresAuth={true}
                                            isVestingRoute={true}
                                            authWallets={AUTHORIZED_VESTING_WALLETS}
                                        />
                                    }
                                />
                                <Route 
                                    path="/admin" 
                                    element={
                                        <ProtectedRoute 
                                            element={Admin}
                                            requiresAuth={true}
                                            isAdminRoute={true}
                                            adminWallet={ADMIN_ADDRESS}
                                        />
                                    } 
                                />
                                
                                {/* Fallback Route */}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Suspense>
                    </main>

                    {/* Footer - Nouveau composant importé */}
                    <Footer />
                </div>
            </Router>
        </ErrorBoundary>
    );
};

export default App;
